import heroPhoto from '../assets/images/main.jpg';
import Button from '../shared/Button.jsx';
import ImageSlot from '../shared/ImageSlot.jsx';
import Reveal from '../shared/Reveal.jsx';
import { useParallax } from '../hooks/useParallax.js';

/* The drift animation and the parallax both want `transform`, so they get a
 * layer each: the outer box is positioned and parallaxed, the inner one drifts.
 *
 * These are anchored to page scroll, not the viewport centre — pinned this near
 * the top of the hero, the viewport anchor resolves to a large negative offset
 * at rest and parks them behind the sticky header. */
function Cloud({ factor, className, drift, cloudClassName }) {
  const ref = useParallax(factor, { anchor: 'page' });

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={`absolute [--drift-x:clamp(36px,8vw,120px)] ${className}`}
    >
      <div
        className={`size-full rounded-full ${cloudClassName}`}
        style={{ animation: drift }}
      />
    </div>
  );
}

function Sun() {
  return (
    <div aria-hidden="true" className="absolute right-[8%] top-14 size-28">
      <div className="size-full rounded-full bg-[radial-gradient(circle_at_35%_35%,#ffe58a,#f7d661)] shadow-[0_0_0_18px_rgb(247_214_97_/_0.22),0_0_0_40px_rgb(247_214_97_/_0.12)]" />
      <div className="absolute inset-0 animate-spin-slow">
        <span className="absolute -top-[22px] left-1/2 -ml-0.5 h-4 w-1 rounded-sm bg-sun/75" />
        <span className="absolute -bottom-[22px] left-1/2 -ml-0.5 h-4 w-1 rounded-sm bg-sun/75" />
        <span className="absolute -left-[22px] top-1/2 -mt-0.5 h-1 w-4 rounded-sm bg-sun/75" />
        <span className="absolute -right-[22px] top-1/2 -mt-0.5 h-1 w-4 rounded-sm bg-sun/75" />
      </div>
    </div>
  );
}

function FloatingCard({ className, animation, value, label, valueClass, labelClass }) {
  return (
    <div
      className={`absolute rounded-[20px] border-2 border-forest px-[15px] py-2.5 ${animation} ${className}`}
    >
      <div
        className={`font-display text-[26px] font-extrabold leading-none ${valueClass}`}
      >
        {value}
      </div>
      <div className={`text-xs font-bold ${labelClass}`}>{label}</div>
    </div>
  );
}

export default function Hero() {
  const imageRef = useParallax(-0.06);

  return (
    <section
      id="top"
      className="relative overflow-hidden bg-[linear-gradient(180deg,#eaf4ec_0%,#f4f7e8_55%,#fbf8f0_100%)] pb-[120px]"
    >
      <Sun />
      {/* Positions scale with the viewport rather than stepping at a breakpoint.
          The ceilings are the design's desktop values; the floors keep both
          clouds clear of the sun, which owns everything past 92vw - 112px. */}
      <Cloud
        factor={0.12}
        className="left-[max(-70px,-10vw)] top-[clamp(12px,3.4vw,50px)] h-[clamp(36px,4.2vw,60px)] w-[clamp(128px,14.6vw,210px)]"
        cloudClassName="bg-white/75"
        drift="drift 34s linear infinite alternate"
      />
      <Cloud
        factor={0.2}
        className="left-[clamp(64px,24vw,38%)] top-[clamp(52px,5.3vw,76px)] h-[clamp(24px,3.3vw,48px)] w-[clamp(64px,9.7vw,140px)]"
        cloudClassName="bg-white/60"
        drift="drift 26s linear infinite alternate-reverse"
      />
      {/*<Cloud
        factor={0.08}
        className="left-[56%] top-[104px] hidden h-[34px] w-24 sm:block"
        cloudClassName="bg-white/50"
        drift="drift 40s linear infinite alternate"
      />*/}

      {/* pt-24 keeps a sky band the clouds can sit in below phone and tablet
          widths; lg goes back to the design's spacing, where the two-column
          layout gives them room and the badge covers any overlap. */}
      <div className="relative mx-auto grid max-w-[1240px] items-center gap-14 px-5 pt-24 sm:px-8 lg:grid-cols-[1.05fr_.95fr] lg:pt-[76px]">
        <div>
          <Reveal className="inline-flex items-center gap-[9px] rounded-full border-2 border-forest bg-white py-[7px] pl-[9px] pr-[15px] text-[13.5px] font-bold text-forest shadow-[0_3px_0_rgb(20_83_45_/_0.16)]">
            <span className="size-[9px] flex-none rounded-full bg-coral animate-pulse-ring" />
            September intake · 6 places left in Toddler class
          </Reveal>

          <Reveal
            as="h1"
            index={1}
            className="mt-5 text-balance font-display text-[clamp(38px,7vw,78px)] font-extrabold leading-[0.97] tracking-[-0.02em] text-forest"
          >
            Every child starts
            <br />
            with{' '}
            <span className="relative isolate inline-block">
              <span
                aria-hidden="true"
                className="absolute -left-1 -right-1 bottom-2 z-0 h-[15px] rounded-full bg-sun"
              />
              <span className="relative z-1">one step</span>
            </span>
            .
          </Reveal>

          <Reveal
            as="p"
            index={2}
            className="mt-[22px] max-w-[520px] text-pretty text-[19.5px] leading-[1.55] text-ink-soft"
          >
            Toddler's Academy is a public school where 18 children share one classroom and
            one teacher who knows every single name. Walk the path with us — from first
            crayon to final exam.
          </Reveal>

          <Reveal index={3} className="mt-8 flex flex-wrap gap-3.5">
            <Button href="#programs" variant="primary">
              Explore our programs
            </Button>
            <Button href="#admissions" variant="outlineSweep">
              Book a campus visit
            </Button>
          </Reveal>

          <Reveal index={0} className="mt-[34px] flex items-center gap-3.5">
            <div className="flex flex-none">
              {['Amaka Bello', 'Tunde Salami', 'Ngozi Eze'].map((parent, i) => (
                <div
                  key={parent}
                  className={`size-[38px] overflow-hidden rounded-full border-[3px] border-cream ${
                    i > 0 ? '-ml-[13px]' : ''
                  }`}
                >
                  <ImageSlot shape="circle" name={parent} alt="" />
                </div>
              ))}
            </div>
            <p className="text-[14.5px] leading-[1.35] text-ink-soft">
              <b className="text-forest">412 families</b> walk with us this year.
              <br />
              94% of them stay through Grade 10.
            </p>
          </Reveal>
        </div>

        <div
          ref={imageRef}
          className="relative mx-auto w-full max-w-[420px] lg:max-w-none"
        >
          {/* main.jpg is a cutout on white, so the frame is white too and the
              blob edge stays clean where the crop runs out. */}
          <div className="relative aspect-[4/4.6] overflow-hidden rounded-[48%_52%_46%_54%_/_54%_46%_54%_46%] border-[5px] border-white bg-white shadow-[0_22px_44px_rgb(20_83_45_/_0.16)]">
            <ImageSlot
              src={heroPhoto}
              alt="A delighted young child laughing towards the camera"
            />
          </div>

          <FloatingCard
            className="left-0 top-4 bg-white shadow-[0_6px_0_rgb(20_83_45_/_0.14)] lg:-left-[34px]"
            animation="animate-bob"
            value="18"
            valueClass="text-coral"
            label="children per class"
            labelClass="text-ink-soft"
          />
          <FloatingCard
            className="bottom-16 right-0 bg-sun shadow-[0_6px_0_rgb(20_83_45_/_0.18)] lg:-right-[30px]"
            animation="animate-bob2"
            value="2–16"
            valueClass="text-forest"
            label="years old, one campus"
            labelClass="text-[#3d3418]"
          />
          <div className="absolute -bottom-1.5 left-[22px] animate-bob2-slow rounded-full border-2 border-forest bg-white px-4 py-2 font-hand text-[19px] text-forest shadow-[0_5px_0_rgb(20_83_45_/_0.14)]">
            no fees. ever.
          </div>
        </div>
      </div>

      <div
        aria-hidden="true"
        className="absolute -bottom-[42px] -left-[6%] -right-[6%] h-[150px] rounded-t-[50%_100%] bg-sage-mid"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-[70px] -left-[12%] -right-[12%] h-[150px] rounded-t-[50%_100%] bg-sage"
      />
      <div className="absolute bottom-3.5 left-1/2 z-2 -ml-[70px] w-[140px] text-center font-hand text-[19px] text-forest">
        ↓ scroll to walk
      </div>
    </section>
  );
}
